"use client";

import React, { useState, useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { FormHistoryItem, getFormHistory, deleteFormHistory } from "@/lib/utils/crm/form-history";
import type { SimpleTableConfig, BaseEntity } from "../UniversalCRMTable/types";

interface FormHistoryTabProps {
  form: UseFormReturn<any>;
  entityName: string;
  config: SimpleTableConfig;
}

/**
 * Tab component that displays form history and allows restoration of previous entries
 */
export function FormHistoryTab({ form, entityName, config }: FormHistoryTabProps) {
  const [history, setHistory] = useState<FormHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load history when component mounts
  useEffect(() => {
    try {
      const items = getFormHistory(entityName);
      setHistory(items);
    } catch (error) {
      console.warn("Failed to load form history:", error);
    } finally {
      setIsLoading(false);
    }
  }, [entityName]);

  // Generate preview text from form data
  const getPreviewText = useMemo(() => {
    return (formData: Record<string, unknown>): string => {
      // Try to find a good preview field (name, title, subject, email, etc.)
      const previewFields = ["name", "title", "subject", "email", "description"];
      for (const field of previewFields) {
        if (field in formData && formData[field]) {
          const value = formData[field];
          if (typeof value === "string") {
            return value.substring(0, 100);
          }
        }
      }
      // Fallback: show first non-empty field
      for (const [key, value] of Object.entries(formData)) {
        if (value && typeof value === "string") {
          return `${key}: ${value.substring(0, 80)}`;
        }
      }
      return "(Empty form)";
    };
  }, []);

  const handleRestore = (item: FormHistoryItem) => {
    // Reset the form with restored values (no validation here — runs on submit)
    form.reset(item.formData as any);
  };

  const handleDelete = (id: string) => {
    deleteFormHistory(entityName, id);

    // Refresh the list
    const updatedItems = getFormHistory(entityName);
    setHistory(updatedItems);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-muted-foreground">Loading history...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {history.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-muted-foreground mb-2">📝</div>
            <div className="text-sm text-muted-foreground">No recent form entries found</div>
          </div>
        </div>
      ) : (
        <>
          <div className="text-xs text-muted-foreground mb-3 px-1">
            {history.length} entr{history.length !== 1 ? "ies" : "y"} found
          </div>
          <div className="max-h-[600px] overflow-y-auto pr-2 space-y-3">
            {history.map((item) => (
              <div
                key={item.id}
                className="group rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm mb-1 line-clamp-2">
                        {getPreviewText(item.formData)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {new Date(item.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100 transition-opacity flex-shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-xs whitespace-nowrap"
                        onClick={() => handleRestore(item)}
                      >
                        Restore
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-xs text-destructive hover:bg-destructive/10"
                        onClick={() => handleDelete(item.id)}
                        aria-label="Delete history entry"
                      >
                        <span className="sr-only">Delete</span>✕
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
